import React, { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTheme, Button, IconButton } from '@mui/material'
import { LogoutOutlined } from '@mui/icons-material'
import ArchitectureOutlined from '@mui/icons-material/ArchitectureOutlined'
import axios from 'axios'
import ConfirmDialog from '@components/ConfirmDialog'
import Footer from '@components/part/Footer'
import useIndicator from '@hooks/useIndicator'
import useAlert from '@hooks/useAlert'
import WebService from '@util/webService'
import { comma } from '@src/util/helper'
import { isAdmin } from '@util/helper'
import { createOrder, validateInputs, checkFileSize, createOrderNo, customSkipKeys } from '@pages/common/common'
import { showSnackbar } from '@context/SnackbarContext'

// 자유결제 상품코드 및 금액 규칙: 최소 1만원, 1만원 단위 (서버 PublicController 와 동일)
const CUSTOM_PRODUCT_CODE = 'P100'
const MIN_AMOUNT = 10000
const AMOUNT_UNIT = 10000

// 금액 입력: 만원 단위 숫자 하나 (예: 25 → 250,000원)
const UNIT_10K = 10000

// main.js(AOS, 헤더/메뉴, 사업자 유형 토글) 를 마운트마다 다시 실행하기 위한 순번
let contractCustomMountSeq = 0

const removeNonDigits = (input) => {
  return input.replace(/\D/g, '')
}

// 금액 버튼 공통 스타일
// 전역 CSS(.step_cont .form_box form .form_row button: 120x62, 흰 글씨)를 덮어쓰기 위해 크기/색을 모두 지정
const amountBtnStyle = {
  height: 30,
  minWidth: 0,
  padding: '0 10px',
  fontSize: 13,
  lineHeight: '28px',
  border: '1px solid #ccc',
  borderRadius: 4,
  background: '#fff',
  color: '#333',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const ContractCustom = () => {
  const [mainJsSrc] = useState(() => `/src/js/main.js?contractCustom=${++contractCustomMountSeq}`)
  const navigate = useNavigate()
  const theme = useTheme()
  const inputsRef = useRef({})
  const confirmRef = useRef(null)
  const codes = useSelector((state) => state.codeReducer)
  const productCodes = codes.filter((r) => r.codeGroupId === 'R001')
  const account = useSelector((state) => state.accountReducer)
  const [openIndicator, closeIndicator, renderIndicator] = useIndicator()
  const { alert, renderAlert } = useAlert()
  const [isLoading, set_isLoading] = useState(true)
  const [order] = useState(() => ({ ...createOrder(), productCode: CUSTOM_PRODUCT_CODE, rmailSvcYn: 'N' }))
  // 만원 단위 입력값, 0에서 시작
  const [count10k, set_count10k] = useState('0')
  const amount = (+count10k || 0) * UNIT_10K
  // 금액 버튼: 만원 단위로 누적
  const addCount10k = (n) => {
    set_count10k((v) => String((+v || 0) + n))
  }
  const [branches, set_branches] = useState([])
  const [branchId, set_branchId] = useState(-1)
  const [payInfo, set_payInfo] = useState({})
  const [bizFiles, set_bizFiles] = useState([])
  const [corpFiles, set_corpFiles] = useState([])

  for (const [key, value] of Object.entries(order)) {
    if (!inputsRef.current[key]) {
      inputsRef.current[key] = { name: key, value: value }
    }
  }

  const productName = productCodes.find((r) => r.code === CUSTOM_PRODUCT_CODE)?.name ?? '자유결제'

  const handleChange = (e) => {
    const digitFields = ['repPhone', 'contactPhone', 'civilNum1', 'civilNum2', 'bizNum1', 'bizNum2', 'bizNum3', 'corpNum1', 'corpNum2']
    if (digitFields.includes(e.target.name)) {
      inputsRef.current[e.target.name].value = removeNonDigits(e.target.value)
    }
  }

  // 계약 지점: 영업 중(S001) + 노출(Y), 노출 조건은 /public/info 에서 이미 적용됨
  const fetchBranches = async () => {
    const url = `/public/info`
    const resp = await WebService.get(url)
    if (resp.repCode !== 'ack') {
      showSnackbar('지점 정보를 가져올 수 없습니다.', theme.palette.warning.dark)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    const list = JSON.parse(repMessage.branches)
      .filter((b) => b.id > 0 && b.statCode === 'S001')
      .sort((a, b) => a.id - b.id)
    set_branches(list)
    // 본점을 기본 선택, 본점이 없으면 첫 지점
    set_branchId((list.find((b) => b.hqYn === 'Y') ?? list[0])?.id ?? -1)
  }

  const fetchPayInfo = async () => {
    const url = `/public/pay/info`
    const resp = await WebService.get(url)
    if (resp.repCode !== 'ack') {
      showSnackbar('결제사 정보를 가져올 수 없습니다.')
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    set_payInfo(JSON.parse(repMessage.payInfo))
  }

  const prepare = () => {
    const branch = branches.find((b) => b.id === branchId)
    if (!branch) {
      showSnackbar('계약 지점을 선택해 주세요.', theme.palette.warning.dark)
      document.getElementById('branchSelect')?.focus()
      return
    }

    if (amount < MIN_AMOUNT || amount % AMOUNT_UNIT !== 0) {
      showSnackbar('결제 금액은 1만원 이상, 1만원 단위로 입력해 주세요.', theme.palette.warning.dark)
      document.getElementById('amountInput')?.focus()
      return
    }

    // 알게되신 경로 입력란 제거: 기본값 '기타'(R003 / C004), validateInputs 전에 지정
    order.channelCode = 'C004'
    // 우편물 관련 입력란 제거: 대리 개봉 및 메일 발송 '불필요', 보관 기간 초과 시 폐기 '동의'
    order.emailSvcYn = 'N'
    order.destroySvcYn = 'Y'

    // 화면에서 제거한 업종/주소/사업자 유형은 검사 제외
    if (!validateInputs(order, inputsRef, theme, customSkipKeys)) {
      return
    }

    order.branchId = branch.id
    order.productCode = CUSTOM_PRODUCT_CODE
    order.rmailSvcYn = 'N'
    order.amount = amount
    return true
  }

  const payRequest = () => {
    if (!payInfo.returnUrl) {
      closeIndicator()
      showSnackbar('결제사 정보를 가져올 수 없습니다(2).')
      return
    }
    window.cookiepayments.payrequest({
      ORDERNO: order.orderNo,
      PRODUCTNAME: productName,
      AMOUNT: order.amount,
      BUYERNAME: order.repName,
      PAYMETHOD: 'CARD',
      PRODUCTCODE: order.productCode,
      BUYERPHONE: order.repPhone,
      RETURNURL: payInfo.returnUrl,
    })
  }

  const submit = async (adminMode) => {
    order.orderNo = createOrderNo()
    if (adminMode) {
      order.statCode = 'S001'
    }

    const formData = new FormData()
    if (bizFiles.length > 0) {
      formData.append('bizFiles', bizFiles[0])
    }
    corpFiles.forEach((f) => {
      formData.append('corpFiles', f)
    })
    formData.append('body', new Blob([JSON.stringify({ order: order })], { type: 'application/json' }))

    openIndicator()
    const resp = await axios.post(`/public/order/custom`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    if (resp.data.repCode !== 'ack') {
      closeIndicator()
      alert(resp.data.repMessage)
      return
    }

    if (adminMode) {
      closeIndicator()
      showSnackbar('처리되었습니다.')
      navigate('/admin')
      return
    }
    payRequest()
  }

  useEffect(() => {
    if (!payInfo.apiId) {
      return
    }
    // App.js 가 결제 스크립트를 async 로 붙이므로, 직접 접속 시 아직 로드 전일 수 있음
    let timer = null
    const tryInit = () => {
      if (window.cookiepayments) {
        window.cookiepayments.init({ api_id: payInfo.apiId })
      }
      else {
        timer = setTimeout(tryInit, 100)
      }
    }
    tryInit()
    return () => clearTimeout(timer)
  }, [payInfo])

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchBranches()
    fetchPayInfo()
    openIndicator()
    const timer = setTimeout(() => {
      set_isLoading(false)
      closeIndicator()
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  if (isLoading) {
    return <>{renderIndicator()}</>
  }

  return (
    <>
      {renderAlert()}

      <Helmet>
        <script src={mainJsSrc}></script>
      </Helmet>
      <header id='header'>
        <div className='container'>
          <div className='logo'>
            <a href='/'>
              <img src='/src/img/logo_w.svg' alt='비상주 오피스' className='white' />
              <img src='/src/img/logo.svg' alt='비상주 오피스' className='black' />
            </a>
          </div>
          <div className='gnb'>
            <nav className='gnb_nav'>
              <ul>
                <li>
                  <a href='#'>비상주 오피스</a>
                  <div className='dropdown'>
                    <a href='/home#about'>비상주오피스 소개</a>
                    <a href='/home#progress'>계약절차안내</a>
                    <a href='/home#contact'>계약,결제하기</a>
                  </div>
                </li>
                <li>
                  <a href='/home#bundle'>결합 할인 서비스</a>
                </li>
                <li>
                  <a href='/home#location'>오시는 길</a>
                </li>
              </ul>
            </nav>
            <div className='inquiry'>
              <a href='/home#contact'>빠른 계약하기</a>
            </div>
            {account?.user?.id ? (
              <>
                {isAdmin(account) ? (
                  <div>
                    <IconButton onClick={() => navigate('/admin')}>
                      <ArchitectureOutlined style={{ color: '#9e9e9e' }} />
                    </IconButton>
                  </div>
                ) : null}
                <div>
                  <IconButton onClick={() => navigate('/logout')}>
                    <LogoutOutlined style={{ color: '#9e9e9e' }} />
                  </IconButton>
                </div>
              </>
            ) : null}
          </div>
          <div className='mo_btn'>
            <a href='#' id='moBtn'>
              <img src='/src/img/nav_on_w.svg' className='white' />
              <img src='/src/img/nav_on_b.svg' className='black' />
            </a>
          </div>
        </div>
        <div className='side_menu'>
          <div className='box'>
            <div className='logo'>
              <a href='/'>
                <img src='/src/img/logo_w.svg' alt='비상주 오피스' />
              </a>
            </div>
            <nav className='side_nav'>
              <ul>
                <li>
                  <a href='#'>비상주 오피스</a>
                  <div className='dropdown'>
                    <a href='/home#about'>비상주오피스 소개</a>
                    <a href='/home#progress'>계약절차안내</a>
                    <a href='/home#contact'>계약,결제하기</a>
                  </div>
                </li>
                <li>
                  <a href='/home#bundle'>결합 할인 서비스</a>
                </li>
                <li>
                  <a href='/home#location'>오시는 길</a>
                </li>
              </ul>
            </nav>
            <div className='inquiry'>
              <a href='/home#contact'>빠른 계약하기</a>
            </div>
            <a href='#' id='moCan'>
              <img src='/src/img/nav_can.svg' />
            </a>
          </div>
        </div>
      </header>
      <div className='sub_banner step_2'>
        <div className='container'>
          <div className='text_box'>
            <h2 data-aos='fade-up' data-aos-duration={1500} data-aos-delay={100}>
              빠르고 합리적으로 계약을 도와드립니다.
            </h2>
          </div>
        </div>
      </div>
      <div className='step_cont'>
        <div className='container'>
          <div className='title'>
            <span data-aos='fade-up' data-aos-duration={1500} data-aos-delay={100}>
              Custom Payment
            </span>
            <h2 data-aos='fade-up' data-aos-duration={1500} data-aos-delay={200}>
              자유결제
            </h2>
            <p data-aos='fade-up' data-aos-duration={1500} data-aos-delay={300}>
              결제 금액과 정보를 입력하시면 바로 결제됩니다.
            </p>
          </div>
          <div className='form_box' data-aos='fade-up' data-aos-duration={1500} data-aos-delay={400}>
            <form>
              <div className='form_row'>
                <div className='form_col'>
                  <p>선택 상품명</p>
                  <div className='input_box'>
                    <input type='text' defaultValue={productName} disabled />
                  </div>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    결제 금액<span className='essential'>*</span>
                  </p>
                  {/* 만원 단위 숫자 입력 + 원 단위 합계 + 금액 버튼(10만원/1만원 누적, 지움) */}
                  <div className='input_box' style={{ flex: '0 0 90px', width: 90 }}>
                    <label>
                      <input
                        type='text'
                        id='amountInput'
                        inputMode='numeric'
                        maxLength={4}
                        style={{ textAlign: 'right' }}
                        value={count10k}
                        onChange={(e) => set_count10k(removeNonDigits(e.target.value))}
                      />
                    </label>
                  </div>
                  <span style={{ whiteSpace: 'nowrap', margin: '0 8px 0 6px' }}>만원</span>
                  {/* 금액 자릿수가 늘어도 버튼 위치가 밀리지 않도록 폭 고정 */}
                  <b style={{ whiteSpace: 'nowrap', width: 140, flexShrink: 0 }}>(₩ {comma(amount)})</b>
                  <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
                    <button type='button' style={amountBtnStyle} onClick={() => addCount10k(10)}>
                      10만원
                    </button>
                    <button type='button' style={amountBtnStyle} onClick={() => addCount10k(1)}>
                      1만원
                    </button>
                    <button
                      type='button'
                      style={{ ...amountBtnStyle, background: '#1f2d4d', color: '#fff', borderColor: '#1f2d4d' }}
                      onClick={() => set_count10k('0')}
                    >
                      지움
                    </button>
                  </div>
                  <span style={{ whiteSpace: 'nowrap', marginLeft: 'auto', fontSize: 13, color: '#888' }}>
                    1만원 이상
                  </span>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    계약 지점명<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <select id='branchSelect' value={branchId} onChange={(e) => set_branchId(+e.target.value)}>
                        {branches.length === 0 ? <option value={-1}>계약 가능한 지점이 없습니다</option> : null}
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.branchName}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    대표자명<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={32}
                        name='repName'
                        defaultValue={order.repName}
                        ref={(el) => (inputsRef.current['repName'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
                <div className='form_col'>
                  <p>
                    대표자 연락처<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='tel'
                        maxLength={11}
                        name='repPhone'
                        defaultValue={order.repPhone}
                        ref={(el) => (inputsRef.current['repPhone'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    담당자명<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={32}
                        name='contactName'
                        defaultValue={order.contactName}
                        ref={(el) => (inputsRef.current['contactName'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
                <div className='form_col'>
                  <p>
                    담당자 연락처<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='tel'
                        maxLength={11}
                        name='contactPhone'
                        defaultValue={order.contactPhone}
                        ref={(el) => (inputsRef.current['contactPhone'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    상호명<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={32}
                        name='companyName'
                        defaultValue={order.companyName}
                        ref={(el) => (inputsRef.current['companyName'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row'>
                <div className='form_col'>
                  <p>
                    이메일<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='email'
                        maxLength={32}
                        name='email'
                        defaultValue={order.email}
                        ref={(el) => (inputsRef.current['email'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
                <div className='form_col'>
                  <p>
                    주민등록번호<span className='essential'>*</span>
                  </p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={6}
                        name='civilNum1'
                        defaultValue={order.civilNum1}
                        ref={(el) => (inputsRef.current['civilNum1'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                  <span>-</span>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={7}
                        name='civilNum2'
                        defaultValue={order.civilNum2}
                        ref={(el) => (inputsRef.current['civilNum2'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row' id='business_cert'>
                <div className='form_col'>
                  <p>사업자등록증</p>
                  <div className='input_box'>
                    <label className='custom_file'>
                      <span className='filename'>
                        {bizFiles.map((f, idx) => (
                          <div key={idx}>{f.name}</div>
                        ))}
                      </span>
                      <input
                        type='file'
                        id='bizFiles'
                        className='input_file'
                        name='file'
                        accept='.jpg, .jpeg, .png'
                        onChange={(e) => {
                          const files = [...e.target.files]
                          if (!checkFileSize(files, theme)) {
                            return
                          }
                          set_bizFiles(files)
                        }}
                      />
                      <button
                        type='button'
                        className='upload_btn'
                        onClick={(e) => {
                          e.stopPropagation()
                          document.getElementById('bizFiles').click()
                        }}
                      >
                        파일 선택
                      </button>
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row' id='business_num'>
                <div className='form_col'>
                  <p>사업자등록번호</p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={3}
                        name='bizNum1'
                        defaultValue={order.bizNum1}
                        ref={(el) => (inputsRef.current['bizNum1'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                  <span>-</span>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={2}
                        name='bizNum2'
                        defaultValue={order.bizNum2}
                        ref={(el) => (inputsRef.current['bizNum2'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                  <span>-</span>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        maxLength={5}
                        name='bizNum3'
                        defaultValue={order.bizNum3}
                        ref={(el) => (inputsRef.current['bizNum3'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row' id='corp_cert'>
                <div className='form_col'>
                  <p>법인등기부등본</p>
                  <div className='input_box'>
                    <label className='custom_file'>
                      <div className='filename' style={{ height: 'inherit' }}>
                        {corpFiles.map((f, idx) => (
                          <div key={idx}>{f.name}</div>
                        ))}
                      </div>
                      <input
                        type='file'
                        id='corpFiles'
                        className='input_file'
                        name='file'
                        accept='.jpg, .jpeg, .png'
                        multiple
                        onChange={(e) => {
                          const files = [...e.target.files]
                          if (!checkFileSize(files, theme)) {
                            return
                          }
                          set_corpFiles(files)
                        }}
                      />
                      <button
                        type='button'
                        className='upload_btn'
                        onClick={(e) => {
                          e.stopPropagation()
                          document.getElementById('corpFiles').click()
                        }}
                      >
                        파일 선택
                      </button>
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row' id='corp_num'>
                <div className='form_col'>
                  <p>법인등록번호</p>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        name='corpNum1'
                        maxLength={6}
                        defaultValue={order.corpNum1}
                        ref={(el) => (inputsRef.current['corpNum1'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                  <span>-</span>
                  <div className='input_box'>
                    <label>
                      <input
                        type='text'
                        name='corpNum2'
                        maxLength={7}
                        defaultValue={order.corpNum2}
                        ref={(el) => (inputsRef.current['corpNum2'] = el)}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className='form_row radio'>
                <div className='form_col'>
                  <p>
                    개인정보 수집 및 이용동의
                    <span className='essential'>*</span>
                  </p>
                  <div className='input_box use_info'>
                    <div className='custom_radio radio'>
                      <label>
                        <input
                          type='radio'
                          name='privacy'
                          defaultChecked={order.privacyYn === 'Y'}
                          onChange={() => {
                            order.privacyYn = 'Y'
                          }}
                        />
                        <span>동의</span>
                      </label>
                      <label>
                        <input
                          type='radio'
                          name='privacy'
                          defaultChecked={order.privacyYn === 'N'}
                          onChange={() => {
                            order.privacyYn = 'N'
                          }}
                        />
                        <span>미동의</span>
                      </label>
                    </div>
                    <span>
                      *{' '}
                      <a href='#privacy_modal' rel='modal:open'>
                        [개인정보취급방침]
                      </a>{' '}
                      확인
                    </span>
                  </div>
                </div>
              </div>
              <div className='btn_box'>
                <a href='/' className='cancle_btn btn'>
                  취소
                </a>
                {account?.user?.id ? (
                  <ConfirmDialog
                    btn={
                      <button type='button' className='submit_btn btn'>
                        무결제 등록
                      </button>
                    }
                    content={`관리자 모드로 결제완료로 처리됩니다.`}
                    preHandle={async () => {
                      return prepare()
                    }}
                    perform={() => {
                      submit(true)
                    }}
                  />
                ) : (
                  <>
                    <button type='button' className='submit_btn btn' onClick={() => confirmRef.current?.click()}>
                      {comma(amount)}원 결제하기
                    </button>
                    <ConfirmDialog
                      btn={
                        <Button ref={confirmRef} style={{ display: 'none' }}>
                          확인
                        </Button>
                      }
                      title={'알림'}
                      content={`${comma(amount)}원을 결제합니다.`}
                      bc={'primary'}
                      preHandle={async () => {
                        return prepare()
                      }}
                      perform={() => {
                        submit(false)
                      }}
                    />
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
      <div>{renderIndicator()}</div>
    </>
  )
}

export default ContractCustom
